import { useState, useEffect, useCallback } from 'react';
import { FaChevronDown, FaTimes } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import _, { throttle } from 'lodash';
import DeleteIcon from '@material-ui/icons/Delete';
import PassengersFilter from './PassengersFilter';
import MultiSelectAutocompleteFilter from '../../Filters/MultiSelectAutocompleteFilter';
// import DropdownSelectFilter from '../../Filters/DropdownSelectFilter';
import PortsFilter from './PortsFilter';
import SingleSelectAutocompleteFilter from '../../Filters/SingleSelectAutocompleteFilter';
import MultiCheckboxSelectFilter from '../../Filters/MultiCheckboxSelectFilter';
import {
  // FiltersInitialValue,
  AutocompleteType,
  // CabinTypeFilterValues,
  DiscountFilterValues,
  DiscountEligibility,
  PortOfDepartureOption,
  TravelTypes,
} from '../../../constants';
import { autocompleteRequest } from '../../../store/slices/autocomplete';
import {
  clearCruiseSearchFilters,
  setCruiseSearchInputs,
  setTripSearchInputs,
} from '../../../store/slices/tripSearch';
import Input from '../../Input';
import PassengerOccupationInput from '../../Input/PassengerOccupationInput';
// import ErrorTooltip from '../../ErrorTooltip';
import CustomerLookup from '../../CustomerLookup';
import TwNativeSelect from '../../TwNativeSelect';
import Button from '../../Button';

const FilterBar = ({
  doShowAllFilters,
  doDisablePassengersInput,
  variant,
  //  formValidations,
}) => {
  const ENABLE_CUSTOMER_LOOKUP = false; // Test well when enabling this flag
  const { track } = useAnalytics();
  const [isExpanded, setIsExpanded] = useState(doShowAllFilters);
  // const [passengerCities, setPassengerCities] = useState(null);
  const { staterooms } = useSelector((store) => store.tripSearch.searchInputs);
  const { travelAgency } = useSelector((store) => store.user);
  const [flightOriginLabel, setFlightOriginLabel] = useState('Flight origin');

  const { cruiseLines, origin, cruiseShips, discounts } = useSelector(
    (store) => store.tripSearch.searchInputs.cruise.filters,
  );

  const { areFiltersEmpty, filters } = useSelector((store) => store.tripSearch.searchInputs.cruise);
  const { portOfDepartureOption } = filters;
  const {
    flightOriginAutocompleteResults,
    cruiseLinesAutocompleteResults,
    cruiseShipsAutocompleteResults,
    // citiesAutocompleteResults,
  } = useSelector((store) => store.autocomplete);
  const dispatch = useDispatch();

  const handleFilterChange = useCallback(
    (field) => (value) => {
      dispatch(
        setCruiseSearchInputs({
          filters: field !== 'staterooms' ? { [field]: value } : {},
          staterooms: field === 'staterooms' ? value : undefined,
        }),
      );
      if (field === 'cruiseLines' && _.isEmpty(value)) {
        dispatch(
          autocompleteRequest({
            autocompleteTypes: AutocompleteType.CRUISE_SHIPS,
            responseType: 'cruiseShipsAutocompleteResults',
            cruiseLineIds: [],
          }),
        );
      }
      track('filterBar.handleFilterChange', { [field]: JSON.stringify(value) });
    },
    [dispatch, track],
  );

  const handleDiscountFilterChange = (field, value) => handleFilterChange(field)(value);

  // oxlint-disable-next-line react/exhaustive-deps
  const handleAutocompleteChange = useCallback(
    throttle(
      (autocompleteTypes, responseType) => (query) => {
        dispatch(
          autocompleteRequest({
            autocompleteTypes,
            responseType,
            query,
          }),
        );
      },
      50,
    ),
    [dispatch],
  );

  const handleClearFilters = () => {
    dispatch(clearCruiseSearchFilters());
    track('filterBar.handleClearFilters');
  };

  const handlePassengerChange = (value, index, propertyName) => {
    const updatedStaterooms = [...staterooms];
    updatedStaterooms[index] = {
      ...updatedStaterooms[index],
      [propertyName]: value,
    };
    dispatch(setTripSearchInputs({ staterooms: updatedStaterooms }));
  };

  const handleChangeCustomer = (selectedCustomer) => {
    const updatedStaterooms = [...staterooms];
    if (selectedCustomer) {
      updatedStaterooms[0] = {
        ...updatedStaterooms[0],
        passengerFirstName: selectedCustomer.firstName,
        passengerMiddleName: selectedCustomer.middleName,
        passengerLastName: selectedCustomer.lastName,
        pastPassengerNumbers:
          selectedCustomer.customerMemberships && selectedCustomer.customerMemberships.length > 0
            ? _.filter(selectedCustomer.customerMemberships, (m) => m.provider === 'CRUISE').map(
                (c) => ({ cruiseLineId: c.cruiseLineId, value: c.membershipId }),
              )
            : [],
        customerId:
          selectedCustomer && selectedCustomer.customerId ? selectedCustomer.customerId : undefined,
        customerEmail:
          selectedCustomer && selectedCustomer.email ? selectedCustomer.email : undefined,
      };
    } else {
      updatedStaterooms[0] = {
        ...updatedStaterooms[0],
        customerEmail: undefined,
        customerId: undefined,
      };
    }
    handleFilterChange('staterooms')(updatedStaterooms);
  };

  const handleChangePastPassengerNumber = (value, roomIndex, pastPassengerIndex, propertyName) => {
    const updatedPastPassengerNumbers = [...staterooms[roomIndex].pastPassengerNumbers];
    updatedPastPassengerNumbers[pastPassengerIndex] = {
      ...updatedPastPassengerNumbers[pastPassengerIndex],
      [propertyName]: value,
    };
    handlePassengerChange(updatedPastPassengerNumbers, roomIndex, 'pastPassengerNumbers');
  };

  const handleAddPastPassenger = (roomIndex) => {
    const updatedPastPassengerNumbers = [...staterooms[roomIndex].pastPassengerNumbers];
    updatedPastPassengerNumbers.push({});
    handlePassengerChange(updatedPastPassengerNumbers, roomIndex, 'pastPassengerNumbers');
  };

  const handleRemovePastPassenger = (roomIndex, ppnIndex) => {
    const updatedPastPassengerNumbers = [...staterooms[roomIndex].pastPassengerNumbers];
    updatedPastPassengerNumbers.splice(ppnIndex, 1);
    handlePassengerChange(updatedPastPassengerNumbers, roomIndex, 'pastPassengerNumbers');
  };

  // useEffect(() => {
  //   if (!cruiseLinesAutocompleteResults || !cruiseLinesAutocompleteResults.length) {
  //     dispatch(
  //       autocompleteRequest({
  //         autocompleteTypes: AutocompleteType.CRUISE_LINES,
  //         responseType: 'cruiseLinesAutocompleteResults',
  //       }),
  //     );
  //   }
  //   // oxlint-disable-next-line react/exhaustive-deps
  // }, []);

  useEffect(() => {
    if (cruiseLines && cruiseLines.length > 0) {
      dispatch(
        autocompleteRequest({
          autocompleteTypes: AutocompleteType.CRUISE_SHIPS,
          responseType: 'cruiseShipsAutocompleteResults',
          cruiseLineIds: cruiseLines.map((item) => Number.parseInt(item.value, 10)),
        }),
      );
    }
    setIsExpanded(
      portOfDepartureOption !== PortOfDepartureOption.ANY || (discounts || []).length > 0,
    );
    // oxlint-disable-next-line react/exhaustive-deps
  }, [cruiseLines]);

  // useEffect(() => {
  //   if (citiesAutocompleteResults.length && citiesAutocompleteResults[0].suggests) {
  //     setPassengerCities([
  //       {
  //         ...citiesAutocompleteResults[0],
  //         suggests: citiesAutocompleteResults[0].suggests.map((item) => ({
  //           ...item,
  //           label: item.label.toUpperCase(),
  //         })),
  //       },
  //     ]);
  //   }
  // }, [citiesAutocompleteResults]);

  useEffect(() => {
    if (
      travelAgency &&
      travelAgency.filterSettings &&
      travelAgency.filterSettings.flightOriginLabel
    ) {
      setFlightOriginLabel(travelAgency.filterSettings.flightOriginLabel);
    }
  }, [travelAgency]);

  const discountFilterOptions = () => {
    const response = staterooms.map((__, roomIndex) =>
      DiscountFilterValues.filter((d) => d.doInputForEachRoom).map((d, filterOptionIndex) => {
        const discount = {
          ...d,
          valueProperty: 'staterooms',
          stateroomIndex: roomIndex,
        };
        if (filterOptionIndex === 0 && staterooms.length > 1) {
          discount.doShowStateroomLabel = true;
        }
        if (d.value === DiscountEligibility.PAST_PASSENGER) {
          discount.inputElement = (
            <div className="grid grid-cols-2 mb-2 gap-1">
              <Input
                type="text"
                autoFocus
                placeholder="First name"
                onChange={(event) =>
                  handlePassengerChange(event.target.value, roomIndex, 'passengerFirstName')
                }
                value={staterooms[roomIndex].passengerFirstName || ''}
                className={`${Input.defaultProps.className.replace('py-3', 'py-2').replace('px-6', 'px-4')} h-full`}
              />
              <Input
                type="text"
                placeholder="Middle name"
                onChange={(event) =>
                  handlePassengerChange(event.target.value, roomIndex, 'passengerMiddleName')
                }
                value={staterooms[roomIndex].passengerMiddleName || ''}
                className={`${Input.defaultProps.className.replace('py-3', 'py-2').replace('px-6', 'px-4')} h-full`}
              />
              <Input
                type="text"
                placeholder="Last name"
                onChange={(event) =>
                  handlePassengerChange(event.target.value, roomIndex, 'passengerLastName')
                }
                value={staterooms[roomIndex].passengerLastName || ''}
                className={`${Input.defaultProps.className.replace('py-3', 'py-2').replace('px-6', 'px-4')} col-span-2 h-full`}
              />
              {(staterooms[roomIndex].pastPassengerNumbers || []).map((ppn, index) => (
                <div key={ppn?.cruiseLineId} className="grid grid-cols-2 gap-1 col-span-2">
                  <div>
                    {cruiseLinesAutocompleteResults &&
                      cruiseLinesAutocompleteResults[0] &&
                      cruiseLinesAutocompleteResults[0].suggests && (
                        <TwNativeSelect
                          value={ppn.cruiseLineId}
                          options={cruiseLinesAutocompleteResults[0].suggests}
                          onChange={(event) =>
                            handleChangePastPassengerNumber(
                              event.target.value,
                              roomIndex,
                              index,
                              'cruiseLineId',
                            )
                          }
                          defaultOptionLabel="Select cruise line..."
                        />
                      )}
                  </div>
                  <div className="flex items-center">
                    <Input
                      type="text"
                      title="Please enter your past passenger number"
                      placeholder="Enter past passenger number"
                      onChange={(event) =>
                        handleChangePastPassengerNumber(
                          event.target.value,
                          roomIndex,
                          index,
                          'value',
                        )
                      }
                      value={ppn.value || ''}
                      className={`${Input.defaultProps.className.replace('py-3', 'py-2').replace('px-6', 'px-4')} h-full`}
                    />
                    {index !== 0 ? (
                      <DeleteIcon
                        onClick={() => handleRemovePastPassenger(roomIndex, index)}
                        className="cursor-pointer"
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              ))}
              <div className="text-right col-span-2">
                <Button
                  variant={Button.buttonVariants.LINK}
                  size={Button.buttonSizes.XS}
                  onClick={() => handleAddPastPassenger(roomIndex)}
                >
                  Add Past Passenger Number
                </Button>
              </div>
            </div>
          );
        } else if (d.value === DiscountEligibility.OCCUPATION) {
          discount.inputElement = (
            <PassengerOccupationInput
              onChange={(value) => handlePassengerChange(value, roomIndex, 'passengerOccupation')}
              value={staterooms[roomIndex].passengerOccupation}
            />
          );
        }
        return discount;
      }),
    );
    return [
      ...DiscountFilterValues.filter(
        (d) =>
          !d.doInputForEachRoom &&
          (!travelAgency ||
            (travelAgency.isAMEXAgency && d.isAMEX) ||
            (travelAgency.isVirtuosoAgency && d.isVirtuoso)),
      ).map((d) => ({
        ...d,
        valueProperty: 'discounts',
      })),
      ...response,
    ];
  };

  const clearButtonComponent = (
    <button
      type="button"
      className={clsx(
        'py-3 px-4 font-medium focus:outline-none max-w-full',
        { 'text-theme': variant === 'outline' },
        { 'text-tabsText-0': !variant },
      )}
      onClick={handleClearFilters}
    >
      Clear filters <FaTimes className="inline-block ml-1 h-3" />
    </button>
  );

  return (
    <>
      <PassengersFilter disabled={doDisablePassengersInput} variant={variant} />
      {(!travelAgency ||
        !travelAgency.tripkitEnabledTransportModes ||
        travelAgency.tripkitEnabledTransportModes.indexOf(TravelTypes.flight) > -1) && (
        <SingleSelectAutocompleteFilter
          onChange={handleFilterChange('origin')}
          options={flightOriginAutocompleteResults}
          onInputChange={handleAutocompleteChange(
            AutocompleteType.FLIGHT_ORIGIN,
            'flightOriginAutocompleteResults',
          )}
          value={origin}
          label={flightOriginLabel}
          variant={variant}
          filterButtonMaxWidth="max-w-filter"
          widthInside="max-w-85"
        />
      )}
      {/* <ErrorTooltip
        title="Resident city is required"
        open={(formValidations && formValidations.city) || false}
        arrow
      >
        <div>
          <SingleSelectAutocompleteFilter
            onChange={handleFilterChange('city')}
            options={passengerCities}
            onInputChange={handleAutocompleteChange(
              AutocompleteType.PASSENGER_CITIES,
              'citiesAutocompleteResults',
            )}
            value={city}
            variant={variant}
            tooltipPrependText="Resident city (for resident rates)"
            label="Resident city (for resident rates)"
            filterButtonMaxWidth="max-w-filter"
          />
        </div>
      </ErrorTooltip> */}
      <MultiSelectAutocompleteFilter
        onChange={handleFilterChange('cruiseLines')}
        label="Cruise lines"
        options={cruiseLinesAutocompleteResults}
        values={cruiseLines}
        variant={variant}
        filterMaxWidthClass="max-w-filter"
        freeSolo={false}
      />
      <MultiSelectAutocompleteFilter
        onChange={handleFilterChange('cruiseShips')}
        label="Cruise ships"
        options={cruiseShipsAutocompleteResults}
        variant={variant}
        values={cruiseShips}
        filterMaxWidthClass="max-w-filter"
        freeSolo={false}
      />
      {/* <DropdownSelectFilter
        label="Cabin type"
        onChange={handleFilterChange('cabinType')}
        options={CabinTypeFilterValues}
        defaultOptionValue={FiltersInitialValue.cruise.filters.cabinType}
        selectedOptionValue={cabinType}
        menuWidth="w-64"
        filterButtonMaxWidth="max-w-filter"
        variant="standard"
      /> */}

      {!doShowAllFilters && (
        <>
          <button
            type="button"
            className="py-3 px-4 text-tabsText-0 font-medium focus:outline-none max-w-1/8"
            onClick={() => setIsExpanded((v) => !v)}
          >
            {isExpanded ? 'Less filters' : 'More filters'}{' '}
            <FaChevronDown className="inline-block ml-1 h-3" />
          </button>
          {!areFiltersEmpty && clearButtonComponent}
        </>
      )}
      {isExpanded && (
        <div className={clsx('flex gap-2', { 'w-full': !doShowAllFilters })}>
          <PortsFilter variant={variant} />
          {ENABLE_CUSTOMER_LOOKUP && (
            <CustomerLookup doShowInHeaderFilter onChange={handleChangeCustomer} />
          )}
          {false && (
            <MultiCheckboxSelectFilter
              options={discountFilterOptions()}
              values={{ discounts, staterooms }}
              onChange={handleDiscountFilterChange}
              label="Discount"
              sortField="discounts"
              variant={variant}
              widthInside="max-w-85"
              menuWidthClass={
                staterooms && _.find(staterooms, (r) => (r.discounts || []).indexOf(4) > -1)
                  ? 'w-96'
                  : undefined
              }
            />
          )}
          {doShowAllFilters && !areFiltersEmpty && clearButtonComponent}
        </div>
      )}
    </>
  );
};
FilterBar.propTypes = {
  doShowAllFilters: PropTypes.bool,
  doDisablePassengersInput: PropTypes.bool,
  variant: PropTypes.string,
  // formValidations: PropTypes.shape(),
};

FilterBar.defaultProps = {
  doShowAllFilters: false,
  doDisablePassengersInput: false,
  variant: undefined,
  // formValidations: {},
};

export default FilterBar;
